import type { GeoPoint } from '../geolocation';
import type { BaseDocument } from '../mongo/base-document.interface';
import type { OptimizationObjective } from '../servicebus/optimization';
import type { StopsQueueRouteSummary } from '../stops-queue/stops-queue.interface';

export const QUEUE_OPTIMIZATION_STATUS = {
  CANCELLED: 'cancelled',
  CONFIRMED: 'confirmed',
  FAILED: 'failed',
  PENDING: 'pending',
  READY: 'ready',
} as const;
export type QueueOptimizationStatus = (typeof QUEUE_OPTIMIZATION_STATUS)[keyof typeof QUEUE_OPTIMIZATION_STATUS];

/** Whether a proposed stop's estimated arrival falls inside, before, or after its queue stop's time slot. */
export const QUEUE_OPTIMIZATION_ARRIVAL_STATUS = { EARLY: 'early', LATE: 'late', ON_TIME: 'on_time' } as const;
export type QueueOptimizationArrivalStatus =
  (typeof QUEUE_OPTIMIZATION_ARRIVAL_STATUS)[keyof typeof QUEUE_OPTIMIZATION_ARRIVAL_STATUS];

export const QUEUE_OPTIMIZATION_ERROR_CODE = {
  ENGINE_FAILED: 'ENGINE_FAILED',
  QUEUE_CHANGED: 'QUEUE_CHANGED',
  TOO_MANY_WAYPOINTS: 'TOO_MANY_WAYPOINTS',
} as const;
export type QueueOptimizationErrorCode =
  (typeof QUEUE_OPTIMIZATION_ERROR_CODE)[keyof typeof QUEUE_OPTIMIZATION_ERROR_CODE];

/** One job placed in the optimized sequence — mirrors `OptimizedWaypoint`, keyed back to its owning task. Dates travel as ISO strings, matching the engine response. */
export interface QueueOptimizationJob {
  estimatedArrivalTime?: string;
  estimatedDepartureTime?: string;
  jobId: string;
  sequence: number;
  taskId: string;
}

/**
 * Consecutive optimized jobs at the same address, grouped into one stop — see
 * `groupOptimizedJobs` in `apps/pharos-back-deliveries/src/queue-optimization/queue-optimization-grouping.ts`.
 */
export interface QueueOptimizationStop {
  /** Whether the first job's estimated arrival falls inside, before, or after the queue stop's time slot. */
  arrivalStatus: QueueOptimizationArrivalStatus;
  /** The first job's estimated arrival — absent when that job has none. */
  estimatedArrivalTime?: string;
  /** The first job's queue-stop address, denormalized for display. */
  fullAddress: string;
  jobs: QueueOptimizationJob[];
  /**
   * Neutral encoded geometry (Google encoded polyline, precision 5) of the leg leading to this
   * stop — from the previous proposed stop, or from `startPosition` for the first one. Stamped by
   * `QueueOptimizationRouteConsumer` once the routing round trip completes; absent until then (or
   * when routing failed/mismatched).
   */
  routePolyline?: string;
}

/** One job the engine could not place, with the provider's own reason when it supplied one — see `OptimizationUnassignedStop`. */
export interface QueueOptimizationUnassignedJob {
  jobId: string;
  reason?: string;
}

export interface QueueOptimizationResult {
  stops: QueueOptimizationStop[];
  totalDistanceInMeters?: number;
  totalTimeInSeconds?: number;
  unassignedJobIds: string[];
  /** Same jobs as `unassignedJobIds`, carrying the engine's reason when it supplied one. Optional and additive — absent on results computed before this field existed. */
  unassignedJobs?: QueueOptimizationUnassignedJob[];
}

/**
 * One request to optimize a driver's remaining queue, from submission to final outcome. The
 * document `id` is the run id — also the Service Bus correlationId for the
 * `on-optimization-request`/`on-optimization-response` round trip. Transient by design: `expiresAt`
 * drives a Mongo TTL purge 30 minutes after creation, regardless of status.
 */
export interface QueueOptimization extends BaseDocument {
  completedAt?: Date;
  /** Stamped when the run transitions to `confirmed` (see `POST /queue/optimizations/:runId/confirm`). */
  confirmedAt?: Date;
  driverId: string;
  errorCode?: QueueOptimizationErrorCode;
  expiresAt: Date;
  objective: OptimizationObjective;
  requestedAt: Date;
  result?: QueueOptimizationResult;
  /**
   * Uuid of the pending routing round trip requested for the proposed order once the engine
   * responds successfully — set alongside `result`, matched back against
   * `RoutingMessageIdentifiers.computationId` by `QueueOptimizationRouteConsumer`. Absent before
   * the engine responds, or when the routing request itself could not be built/published (the run
   * goes straight to `ready` without polylines in that case).
   */
  routeRequestId?: string;
  /** Ids of jobs left out of the request because they had no resolvable coordinates. */
  skippedJobIds: string[];
  /** sha256 over the exact optimization input sent to the engine — see the hash builder for the exact rule. */
  snapshotHash: string;
  /** The start request's position — the origin of the first proposed stop's `routePolyline`. */
  startPosition: GeoPoint;
  status: QueueOptimizationStatus;
  stopsQueueId: string;
}

export type CreateQueueOptimization = Omit<
  QueueOptimization,
  'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'
>;

// ─── REST contract ───────────────────────────────────────────────────────────

export interface StartQueueOptimizationBody {
  objective: OptimizationObjective;
  /** Driver's current GPS position — the optimization's start point. */
  position: GeoPoint;
}

export interface StartQueueOptimizationResponse {
  runId: string;
}

/**
 * API view of a run — omits internal-only fields (`driverId`, `snapshotHash`, `stopsQueueId`,
 * `expiresAt`, `routeRequestId`, `startPosition`). `queueRouteSummary` is the live queue's
 * `routeSummary` at read time — never stored on the run itself.
 */
export type QueueOptimizationRun = Pick<
  QueueOptimization,
  | 'completedAt'
  | 'confirmedAt'
  | 'errorCode'
  | 'id'
  | 'objective'
  | 'requestedAt'
  | 'result'
  | 'skippedJobIds'
  | 'status'
> & {
  queueRouteSummary?: StopsQueueRouteSummary;
};
